import React, { useState, useEffect, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend } from 'recharts';
import { UserService, User } from '../../services/user';
import { resolveTeamName } from '../../utils/userNameResolver';

interface TeamPerformanceComparisonChartProps {
  data: Array<{
    team_name: string | number;
    success_rate: number;
    collaboration_score: number;
    knowledge_sharing_index: number;
    mentee_development_rate: number;
    workshop_count: number;
    performance_score: number;
  }>;
  loading?: boolean;
}

const TeamPerformanceComparisonChart: React.FC<TeamPerformanceComparisonChartProps> = ({ data, loading = false }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);

  // Load users on mount
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const users = await UserService.getAllDirectoryUsers();
        setUsers(users);
      } catch (error) {
        console.error('Failed to load users:', error);
      } finally {
        setUsersLoading(false);
      }
    };
    loadUsers();
  }, []);

  // Create a map of user ID to user name
  const userIdToNameMap = useMemo(() => {
    const map = new Map<number, string>();
    users.forEach(user => {
      map.set(user.id, user.full_name || user.username);
    });
    return map;
  }, [users]);

  // Resolve team names (convert IDs to names, handling both number and string IDs)
  const resolvedData = useMemo(() => {
    return data.map(item => ({
      ...item,
      team_name: resolveTeamName(item.team_name, userIdToNameMap)
    }));
  }, [data, userIdToNameMap]);

  if (loading || usersLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Team Performance Comparison</h3>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Team Performance Comparison</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No team performance data available
        </div>
      </div>
    );
  }

  const getPerformanceLevel = (score: number) => {
    if (score >= 80) return { level: 'Excellent', color: '#10b981' };
    if (score >= 60) return { level: 'Good', color: '#3b82f6' };
    if (score >= 40) return { level: 'Average', color: '#f59e0b' };
    return { level: 'Needs Improvement', color: '#ef4444' };
  };

  const maxWorkshops = Math.max(...resolvedData.map(d => d.workshop_count));
  const maxScore = Math.max(...resolvedData.map(d => d.performance_score));

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Team Performance Comparison</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={resolvedData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="team_name" 
              tick={{ fontSize: 12 }}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis 
              yAxisId="left"
              tick={{ fontSize: 12 }}
              domain={[0, 'dataMax + 2']}
            />
            <YAxis 
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 12 }}
              domain={[0, 100]}
            />
            <Tooltip 
              formatter={(value: number, name: string) => {
                if (name === 'workshop_count') return [`${value} workshops`, 'Workshop Count'];
                if (name === 'success_rate') return [`${value.toFixed(1)}%`, 'Success Rate'];
                if (name === 'collaboration_score') return [`${value.toFixed(1)}%`, 'Collaboration Score'];
                if (name === 'knowledge_sharing_index') return [`${value.toFixed(1)}%`, 'Knowledge Sharing'];
                if (name === 'mentee_development_rate') return [`${value.toFixed(1)}%`, 'Mentee Development'];
                if (name === 'performance_score') return [`${value.toFixed(1)}%`, 'Performance Score'];
                return [value, name];
              }}
              labelFormatter={(label) => `Team: ${label}`}
            />
            <Legend />
            <Bar 
              yAxisId="left"
              dataKey="workshop_count" 
              name="Workshop Count"
              radius={[4, 4, 0, 0]}
            >
              {resolvedData.map((entry, index) => {
                const performance = getPerformanceLevel(entry.performance_score);
                return (
                  <Cell 
                    key={`cell-count-${index}`} 
                    fill={performance.color} 
                  />
                );
              })}
            </Bar>
            <Bar 
              yAxisId="right"
              dataKey="success_rate" 
              name="Success Rate (%)"
              radius={[4, 4, 0, 0]}
              fill="#8b5cf6"
              opacity={0.7}
            />
            <Bar 
              yAxisId="right"
              dataKey="collaboration_score" 
              name="Collaboration Score (%)"
              radius={[4, 4, 0, 0]}
              fill="#10b981"
              opacity={0.6}
            />
            <Bar 
              yAxisId="right"
              dataKey="knowledge_sharing_index" 
              name="Knowledge Sharing (%)"
              radius={[4, 4, 0, 0]}
              fill="#f59e0b"
              opacity={0.5}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Team Rankings</h4>
          <div className="space-y-2">
            {[...resolvedData]
              .sort((a, b) => b.performance_score - a.performance_score)
              .map((item, index) => {
                const performance = getPerformanceLevel(item.performance_score);
                return (
                  <div key={`${item.team_name}-${index}`} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div className="flex items-center">
                      <span className="text-sm font-medium mr-2">#{index + 1}</span>
                      <div 
                        className="w-3 h-3 rounded mr-2" 
                        style={{ backgroundColor: performance.color }}
                      ></div>
                      <span className="text-sm font-medium">{item.team_name}</span>
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">{item.workshop_count}</span> workshops
                      <span className={`ml-2 text-xs px-2 py-1 rounded`} style={{ 
                        backgroundColor: performance.color + '20', 
                        color: performance.color 
                      }}>
                        {performance.level}
                      </span>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Performance Insights</h4>
          <div className="space-y-3">
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-blue-600">Most Active</span>
                <span className="font-bold text-blue-600">
                  {resolvedData.reduce((max, item) => item.workshop_count > max.workshop_count ? item : max, resolvedData[0])?.team_name}
                </span>
              </div>
              <div className="text-xs text-blue-500 mt-1">
                {Math.max(...resolvedData.map(d => d.workshop_count))} workshops
              </div>
            </div>
            <div className="bg-green-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-green-600">Best Performance</span>
                <span className="font-bold text-green-600">
                  {resolvedData.reduce((best, item) => item.performance_score > best.performance_score ? item : best, resolvedData[0])?.team_name}
                </span>
              </div>
              <div className="text-xs text-green-500 mt-1">
                {Math.max(...resolvedData.map(d => d.performance_score)).toFixed(1)}% score
              </div>
            </div>
            <div className="bg-purple-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-purple-600">Top Collaborators</span>
                <span className="font-bold text-purple-600">
                  {resolvedData.reduce((max, item) => item.collaboration_score > max.collaboration_score ? item : max, resolvedData[0])?.team_name}
                </span>
              </div>
              <div className="text-xs text-purple-500 mt-1">
                {Math.max(...resolvedData.map(d => d.collaboration_score)).toFixed(1)}% collaboration
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamPerformanceComparisonChart;
